import MarqueeRow from "@/components/MarqueeRow";
import { ShoppingBag, ArrowRight } from "lucide-react";
import { proxied } from "@/lib/api";

const inr = (n) => "₹" + Number(n || 0).toLocaleString("en-IN");

const KitCard = ({ p }) => {
  const hasDiscount = p.discount_price && p.price > p.discount_price;
  const bullets = Array.isArray(p.bullets) && p.bullets.length > 0
    ? p.bullets
    : (p.description ? [p.description] : []);

  return (
    <div
      data-cursor="button"
      data-testid={`combo-kit-${p.id}`}
      className="flex-shrink-0 w-[270px] md:w-[310px] bg-white rounded-2xl overflow-hidden border border-[rgba(45,40,37,0.08)] hover:shadow-[0_25px_50px_rgba(139,35,35,0.14)] transition-all"
    >
      <div className="aspect-square overflow-hidden bg-[#F3EBE1] relative">
        <img src={proxied(p.image)} alt={p.name} className="w-full h-full object-cover" />
        {hasDiscount && (
          <div className="absolute top-3 right-3 bg-[#2E7D32] text-white text-[10px] px-2.5 py-1 rounded-full tracking-widest uppercase">
            {Math.round(((p.price - p.discount_price) / p.price) * 100)}% Off
          </div>
        )}
      </div>
      <div className="p-4 md:p-5">
        <h3 className="font-serif text-lg md:text-xl text-[#2D2825] leading-tight line-clamp-1">
          {p.name}
        </h3>
        {p.benefits && (
          <p className="text-[11px] md:text-xs text-[#8B2323] mt-1 line-clamp-1 font-medium">
            {p.benefits}
          </p>
        )}

        {/* Red-dot bullets */}
        {bullets.length > 0 && (
          <ul className="mt-3 space-y-1.5">
            {bullets.slice(0, 4).map((b, idx) => (
              <li key={idx} className="flex items-start gap-2 text-[11px] md:text-xs text-[#2D2825]">
                <span className="w-1.5 h-1.5 rounded-full bg-[#8B2323] mt-1.5 flex-shrink-0" />
                <span className="leading-snug">{b}</span>
              </li>
            ))}
          </ul>
        )}

        {/* Pricing - strikethrough */}
        <div className="mt-4 flex items-baseline gap-2 pt-3 border-t border-[rgba(45,40,37,0.08)]">
          {hasDiscount ? (
            <>
              <span className="font-serif text-lg md:text-xl text-[#8B2323] font-semibold">
                {inr(p.discount_price)}
              </span>
              <span className="font-serif text-lg md:text-xl text-black line-through decoration-black decoration-[1.5px]">
                {inr(p.price)}
              </span>
            </>
          ) : (
            <span className="font-serif text-lg md:text-xl text-[#8B2323] font-semibold">
              {inr(p.price)}
            </span>
          )}
        </div>
      </div>
    </div>
  );
};

const ComboKitsMarquee = ({ items = [], settings = {} }) => {
  const shopLink = settings.combo_shop_link;
  if (!items.length) return null;
  const half = Math.ceil(items.length / 2);
  const row1 = items.slice(0, half);
  const row2 = items.slice(half);
  const buttonHref = "https://rudralife.com/category/shravan-2026?slug=shravan-2026" || "https://rudralife.com/rare-collection";

  return (
    <section id="combo-kits" className="bg-[#FAF6F0] py-14 md:py-24">
      <div className="max-w-7xl mx-auto px-5 md:px-12 mb-8 md:mb-14 text-center">
        <div className="text-[#8B2323] text-[10px] md:text-sm tracking-[0.4em] md:tracking-[0.5em] uppercase mb-3">
          {settings.combo_kicker || "Combinations Crafted With Purpose"}
        </div>
        <h2 className="font-serif text-3xl md:text-6xl italic tracking-tight text-[#C89838] leading-tight">
          {settings.combo_title_line1 || "The Rudraksha"}
          <br className="hidden md:block" /> {settings.combo_title_line2 || "Combo Kits"}
        </h2>
        <div className="h-[2px] w-16 bg-[#D4AF37] mt-5 mx-auto" />
      </div>

      <div className="space-y-5">
        {row1.length > 0 && (
          <MarqueeRow direction="right" speed={30} gap={20} noPause>
            {row1.map((p) => <KitCard key={`c1-${p.id}`} p={p} />)}
          </MarqueeRow>
        )}
        {row2.length > 0 && (
          <MarqueeRow direction="left" speed={30} gap={20} noPause>
            {row2.map((p) => <KitCard key={`c2-${p.id}`} p={p} />)}
          </MarqueeRow>
        )}
      </div>

      {/* Two-liner + single Shop Now button */}
      <div className="max-w-3xl mx-auto px-6 mt-12 md:mt-16 text-center">
        <p className="font-serif italic text-lg md:text-2xl text-[#2D2825] leading-snug">
          {settings.combo_footer_line || "Every kit is Lab-Certified, Vedically Energised and hand-picked for its sacred purpose."}
        </p>
        <a
          href={buttonHref}
          target="_blank"
          rel="noreferrer"
          data-testid="combo-shop-now"
          data-cursor="button"
          className="mt-6 md:mt-8 inline-flex items-center gap-3 px-8 md:px-10 py-3.5 md:py-4 rounded-full bg-[#8B2323] hover:bg-[#6B1A1A] text-white text-xs md:text-sm tracking-[0.35em] uppercase font-medium transition-all hover:shadow-[0_18px_38px_rgba(139,35,35,0.4)] hover:-translate-y-0.5"
        >
          <ShoppingBag size={16} /> {settings.combo_shop_cta || "Shop Now"}
          <ArrowRight size={16} />
        </a>
      </div>
    </section>
  );
};

export default ComboKitsMarquee;
